import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatRadioChange } from "@angular/material/radio";
import {
	Ets1SessionColumnVM,
	Ets1SessionTreeItem,
	filterSessionTreeBySearch,
	hasEts1RadioChildren,
	layoutLabeledSessionColumns,
} from "src/app/cbta-web/ets1/session/ets1-session-layout.utils";
import {
	ets1RadioRequiredError,
	sessionRemarkRequiredError,
} from "src/app/cbta-web/ets1/session/ets1-session-validation.utils";
import { Ts1SessionRemarkModalComponent } from "../ts1-session-remark-modal/ts1-session-remark-modal.component";

export type Ets1AocSessionSection = {
	title: string;
	columns: Ets1SessionColumnVM[];
	code?: string;
	sessions?: UntypedFormGroup;
	sessionRemarks?: UntypedFormGroup;
	optionalRemarks?: boolean;
};

@Component({
	selector: "app-ets1-aoc-session-config",
	templateUrl: "./ets1-aoc-session-config.component.html",
	styleUrls: ["./ets1-aoc-session-config.component.scss"],
	changeDetection: ChangeDetectionStrategy.Default,
	standalone: false,
})
export class Ets1AocSessionConfigComponent {
	@Input({ required: true }) sessions!: UntypedFormGroup;
	@Input({ required: true }) sessionRemarks!: UntypedFormGroup;
	@Input({ required: true }) columns: Ets1SessionColumnVM[] = [];
	/** eTS-3 / eTS-5: one search, then one 4-column grid with a heading per section. TS1 leaves this empty. */
	@Input() sections: Ets1AocSessionSection[] = [];
	@Input() readOnly = false;
	/** Show the remark editor for every selected session, not only required-text rows. */
	@Input() optionalRemarks = false;

	searchQuery = "";

	gridColumns(section?: Ets1AocSessionSection): Ets1SessionColumnVM[] {
		const source = section?.columns ?? this.columns;
		return filterSessionTreeBySearch(source, this.searchQuery);
	}

	get labeledColumns() {
		return layoutLabeledSessionColumns(
			this.sections.map((section) => ({
				...section,
				columns: filterSessionTreeBySearch(section.columns, this.searchQuery),
			})),
		);
	}

	sessionsFor(section?: Ets1AocSessionSection): UntypedFormGroup {
		return section?.sessions ?? this.sessions;
	}

	remarksFor(section?: Ets1AocSessionSection): UntypedFormGroup {
		return section?.sessionRemarks ?? this.sessionRemarks;
	}

	optionalRemarksFor(section?: Ets1AocSessionSection): boolean {
		if (section?.optionalRemarks != null) return section.optionalRemarks;
		return this.optionalRemarks;
	}

	isSelected(code: string, sessions: UntypedFormGroup = this.sessions): boolean {
		return sessions.get(code)?.value === true;
	}

	getRemarkPreview(code: string, remarks: UntypedFormGroup = this.sessionRemarks): string {
		const value = (remarks.get(code)?.value ?? "").toString().trim();
		if (!value) return "";
		return value.length > 40 ? `${value.slice(0, 40)}…` : value;
	}

	showRemarkRow(
		item: Ets1SessionTreeItem,
		sessions: UntypedFormGroup = this.sessions,
		allowOptional = this.optionalRemarks,
	): boolean {
		if (!this.isSelected(item.code, sessions) || item.selectionMode === "radio_option") return false;
		return item.requiresText || allowOptional;
	}

	showRemarkRequiredError(code: string, remarks: UntypedFormGroup = this.sessionRemarks): boolean {
		return sessionRemarkRequiredError(code, remarks);
	}

	showRadioChildren(item: Ets1SessionTreeItem, sessions: UntypedFormGroup = this.sessions): boolean {
		return hasEts1RadioChildren(item) && this.isSelected(item.code, sessions);
	}

	showRadioRequiredError(item: Ets1SessionTreeItem, sessions: UntypedFormGroup = this.sessions): boolean {
		return ets1RadioRequiredError(item.code, sessions);
	}

	selectedRadioChildCode(item: Ets1SessionTreeItem, sessions: UntypedFormGroup = this.sessions): string | null {
		return item.children.find((child) => this.isSelected(child.code, sessions))?.code ?? null;
	}

	onRadioGroupChange(
		item: Ets1SessionTreeItem,
		event: MatRadioChange,
		sessions: UntypedFormGroup = this.sessions,
	): void {
		if (this.readOnly) return;
		const childCode = event.value?.toString().trim();
		if (!childCode) return;
		this.selectRadioOption(item, childCode, sessions);
	}

	selectRadioOption(item: Ets1SessionTreeItem, childCode: string, sessions: UntypedFormGroup = this.sessions): void {
		if (this.readOnly) return;
		for (const child of item.children) {
			sessions.get(child.code)?.setValue(child.code === childCode, { emitEvent: false });
		}
		if (item.selectionMode === "checkbox" && !this.isSelected(item.code, sessions)) {
			sessions.get(item.code)?.setValue(true, { emitEvent: false });
		}
		const parentCtrl = sessions.get(item.code);
		if (parentCtrl?.hasError("requiredRadio")) {
			const { requiredRadio: _removed, ...rest } = parentCtrl.errors ?? {};
			parentCtrl.setErrors(Object.keys(rest).length ? rest : null);
		}
		sessions.markAsDirty();
		sessions.updateValueAndValidity({ emitEvent: true });
	}

	openRemarkEditor(item: Ets1SessionTreeItem, remarks: UntypedFormGroup = this.sessionRemarks): void {
		if (this.readOnly) return;
		const dialogRef = this.dialog.open(Ts1SessionRemarkModalComponent, {
			panelClass: ["ts1-session-remark-modal-panel", "ts1-session-remark-modal"],
			width: "720px",
			hasBackdrop: true,
			data: {
				sessionLabel: item.description,
				value: (remarks.get(item.code)?.value ?? "").toString(),
			},
		});

		dialogRef.afterClosed().subscribe((result: string | null) => {
			if (result === null) return;
			const ctrl = remarks.get(item.code) as UntypedFormControl | null;
			ctrl?.setValue(result);
			if (String(result).trim()) {
				ctrl?.setErrors(null);
			}
			ctrl?.markAsDirty();
			ctrl?.updateValueAndValidity({ emitEvent: false });
		});
	}

	constructor(private dialog: MatDialog) {}
}
